import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { qaReviewQueueApi } from '../../services/qaReviewQueueApi';
import type { QaQueueEntry } from '../../services/qaReviewQueueApi';
import { formatRelativeTime as formatTime } from '../../utils/dateFormat';

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'all';

export default function QaReviewQueue() {
  const [filter, setFilter] = useState<StatusFilter>('pending');
  const [entries, setEntries] = useState<QaQueueEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await qaReviewQueueApi.list(filter);
      setEntries(data.entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = useCallback(async (entry: QaQueueEntry) => {
    if (!confirm(
      `Approve this Q&A and add it to the trained Q&A corpus?\n\nQ: ${entry.question.slice(0, 80)}…`
    )) return;
    setActingOn(entry.id);
    try {
      await qaReviewQueueApi.approve(entry.id, {});
      // Refetch rather than filter locally: the "all" filter needs to show
      // the entry in its new state, not drop it.
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setActingOn(null);
    }
  }, [load]);

  const reject = useCallback(async (entry: QaQueueEntry) => {
    const reason = prompt(
      `Reject this draft? Optionally provide a reason (visible in history):`,
      '',
    );
    // `prompt` returns null on cancel; empty string is a valid "no reason".
    if (reason === null) return;
    setActingOn(entry.id);
    try {
      await qaReviewQueueApi.reject(entry.id, reason || undefined);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Reject failed');
    } finally {
      setActingOn(null);
    }
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Review Queue</h2>
          <p className="text-xs text-gray-500">
            Draft Q&amp;As auto-generated from verified Issue Analysis RCAs. Approved entries are
            added to the trained Q&amp;A corpus and searchable in chat.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={e => setFilter(e.target.value as StatusFilter)}
            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={load}
            className="flex items-center gap-1 text-xs text-gray-600 border border-gray-300 rounded px-2 py-1 hover:bg-gray-100"
            disabled={loading}
            title="Reload"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Reload
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <p className="text-sm text-gray-500 italic py-8 text-center">
          {filter === 'pending'
            ? 'No drafts awaiting review. Drafts appear here automatically when analysts mark Issue Analysis RCAs as verified.'
            : `No ${filter} entries.`}
        </p>
      )}

      <div className="space-y-3">
        {entries.map(entry => (
          <div
            key={entry.id}
            className="p-4 bg-white border border-gray-200 rounded-lg space-y-3"
          >
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded font-medium">
                {entry.ticket_key}
              </span>
              {entry.module && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                  {entry.module}
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded capitalize ${
                  entry.status === 'pending'
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : entry.status === 'approved'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {entry.status}
              </span>
              <span className="text-gray-400">· drafted {formatTime(entry.created_at)}</span>
              {entry.reviewed_by && (
                <span className="text-gray-400">
                  · reviewed by {entry.reviewed_by}
                  {entry.reviewed_at ? ` ${formatTime(entry.reviewed_at)}` : ''}
                </span>
              )}
            </div>

            <div>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                Question
              </span>
              <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{entry.question}</p>
            </div>

            <div>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                Answer
              </span>
              <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{entry.answer}</p>
            </div>

            {entry.rejection_reason && (
              <p className="text-xs text-red-600 italic">
                Rejection reason: {entry.rejection_reason}
              </p>
            )}

            {entry.status === 'pending' && (
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => approve(entry)}
                  disabled={actingOn === entry.id}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle2 size={12} />
                  Approve
                </button>
                <button
                  onClick={() => reject(entry)}
                  disabled={actingOn === entry.id}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle size={12} />
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
